import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, Download, Filter, X, ChevronLeft, ChevronRight, Flame, Zap, Snowflake } from "lucide-react";
import { listLeads, exportCsvBlob } from "@/lib/admin-api";
import { formatMtyDateShort, formatMtyTime } from "@/lib/mty-date";
import { toast } from "sonner";
import { STATUS_LABELS, STATUS_BADGE, tempBadgeClass, tempLabel } from "@/lib/lead-constants";

const PAGE = 25;

function TempMini({ temp }) {
  const Icon = temp === "hot" ? Flame : temp === "warm" ? Zap : Snowflake;
  return (
    <span
      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold ${tempBadgeClass(temp)}`}
      title={`Lead ${tempLabel(temp)}`}
    >
      <Icon className="w-2.5 h-2.5" />
    </span>
  );
}

export default function AdminLeads() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState("");
  const [fuente, setFuente] = useState("");
  const [estatus, setEstatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listLeads({ limit: PAGE, skip, fuente, estatus, q });
      setItems(data.items);
      setTotal(data.total);
    } catch {
      toast.error("Error cargando leads");
    } finally {
      setLoading(false);
    }
  }, [skip, fuente, estatus, q]);

  useEffect(() => { load(); }, [load]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setSkip(0);
    load();
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const blob = await exportCsvBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `flexshade_leads_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch {
      toast.error("Error exportando CSV");
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setQ(""); setFuente(""); setEstatus(""); setSkip(0);
  };

  const page = Math.floor(skip / PAGE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <div className="p-5 md:p-8 max-w-7xl">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h1 className="section-title text-3xl md:text-4xl font-black">Leads</h1>
          <p className="text-neutral-500 text-sm mt-1">{total} lead{total !== 1 ? "s" : ""} totales</p>
        </div>
        <button onClick={exportCsv} disabled={exporting} className="btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm disabled:opacity-60" data-testid="export-csv">
          <Download className="w-4 h-4" /> {exporting ? "Exportando..." : "Exportar CSV"}
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-neutral-200 rounded-2xl p-4 mb-4 flex flex-col md:flex-row gap-2 md:items-center">
        <form onSubmit={onSearchSubmit} className="flex-1 flex gap-2">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre, teléfono, email, ciudad..."
              className="form-input pl-9"
              data-testid="search-input"
            />
          </div>
        </form>
        <div className="flex gap-2">
          <select value={fuente} onChange={(e) => { setFuente(e.target.value); setSkip(0); }} className="form-input" data-testid="filter-fuente">
            <option value="">Todas fuentes</option>
            <option value="cotizacion">Cotización</option>
            <option value="catalogo">Catálogo</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="directo">Directo</option>
          </select>
          <select value={estatus} onChange={(e) => { setEstatus(e.target.value); setSkip(0); }} className="form-input" data-testid="filter-estatus">
            <option value="">Todos estatus</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          {(q || fuente || estatus) && (
            <button onClick={clearFilters} className="px-3 py-2 text-neutral-500 hover:text-neutral-900 text-sm" aria-label="Limpiar filtros">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr className="text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">Nombre</th>
                <th className="px-4 py-3">Score</th>
                <th className="px-4 py-3">Empresa</th>
                <th className="px-4 py-3">Contacto</th>
                <th className="px-4 py-3">Espacio</th>
                <th className="px-4 py-3">Ciudad</th>
                <th className="px-4 py-3">Responsable</th>
                <th className="px-4 py-3">Próximo</th>
                <th className="px-4 py-3">Fuente</th>
                <th className="px-4 py-3">Estatus</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {loading ? (
                <tr><td colSpan={11} className="px-4 py-10 text-center text-neutral-400">Cargando...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={11} className="px-4 py-10 text-center text-neutral-400">No se encontraron leads con esos filtros.</td></tr>
              ) : items.map((l) => (
                <tr key={l.id} className="hover:bg-neutral-50 cursor-pointer" onClick={() => window.location.assign(`/admin/leads/${l.id}`)} data-testid={`lead-row-${l.id}`}>
                  <td className="px-4 py-3 text-neutral-500 whitespace-nowrap">
                    {formatMtyDateShort(l.created_at)}
                    <br/>
                    <span className="text-[10px] text-neutral-400">{formatMtyTime(l.created_at)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/admin/leads/${l.id}`} className="font-semibold text-neutral-900 hover:text-red-600">{l.nombre}</Link>
                    {l.cargo && <div className="text-[10px] text-neutral-500">{l.cargo}</div>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <TempMini temp={l.lead_temp || "cold"} />
                      <span className="text-xs font-semibold text-neutral-700">{l.lead_score ?? 0}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-neutral-600">{l.empresa || "—"}</td>
                  <td className="px-4 py-3 text-neutral-600">
                    <div>{l.telefono}</div>
                    {l.email && <div className="text-xs text-neutral-500">{l.email}</div>}
                  </td>
                  <td className="px-4 py-3 text-neutral-600 capitalize">{l.tipo_espacio || "-"}</td>
                  <td className="px-4 py-3 text-neutral-600">{l.ciudad || "-"}</td>
                  <td className="px-4 py-3 text-neutral-600 text-xs">{l.responsable || "—"}</td>
                  <td className="px-4 py-3 text-xs">
                    {l.next_followup_at ? (
                      <span className="text-orange-700 font-semibold whitespace-nowrap">{formatMtyDateShort(l.next_followup_at)}</span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-semibold px-2 py-1 bg-neutral-100 text-neutral-700 rounded-full">
                      {l.fuente}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full border ${STATUS_BADGE[l.estatus] || ""}`}>
                      {STATUS_LABELS[l.estatus] || l.estatus}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > PAGE && (
          <div className="px-4 py-3 border-t border-neutral-100 flex items-center justify-between text-sm">
            <span className="text-neutral-500">Página {page} de {pages}</span>
            <div className="flex gap-1">
              <button
                onClick={() => setSkip(Math.max(0, skip - PAGE))}
                disabled={skip === 0}
                className="p-2 rounded-lg hover:bg-neutral-100 disabled:opacity-40"
              ><ChevronLeft className="w-4 h-4"/></button>
              <button
                onClick={() => setSkip(skip + PAGE)}
                disabled={skip + PAGE >= total}
                className="p-2 rounded-lg hover:bg-neutral-100 disabled:opacity-40"
              ><ChevronRight className="w-4 h-4"/></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
